import AppContext from 'App/AppContext';
import { IPStack } from 'baseComponents';
import GenericModal from 'components/Dialogs/GenericModal';
import TaskTypeFormRenderer from 'components/Tasks/Forms/TaskTypeFormRenderer';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import KPI from 'models/kpi/kpi';
import Task from 'models/tasks/task';
import TaskType from 'models/tasks/taskType';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetKPIsAndFields } from 'services/Api/kpiService';
import { apiRequest } from 'services/Auth/authConfig';

interface IFormVersionPreviewModalProps {
  // The version to show, with its own name and number for the title.
  taskType: TaskType;
  // The version's fields, sorted and seeded for design-time rendering (prepareDesignTimeFormElements).
  formElements: TaskTypeFormElement[];
  isOpen: boolean;
  onDismiss: () => void;
}

// The form of one version as the people filling it in saw it: read-only, no toolbar, no answers. The
// history modal lists the fields as a table; this is the same list drawn as the form itself.
const FormVersionPreviewModal = (props: IFormVersionPreviewModalProps) => {
  const { t } = useTranslation(['forms']);
  const appContext = useContext(AppContext);
  // Only expression fields read these, to name the fields their formula refers to; the rest of the
  // form does not wait for them.
  const [kpis, setKPIs] = useState<KPI[]>([]);

  // The renderer draws a form ON a task. A definition has none, so it gets an empty one that only
  // names the version - the same stand-in the form editor renders with.
  const task = useMemo(() => {
    const dummy = new Task();
    dummy.taskTypeId = props.taskType.taskTypeId;

    return dummy;
  }, [props.taskType.taskTypeId]);

  useEffect(() => {
    if (!props.isOpen) return;

    const loadKPIs = async () => {
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        setKPIs(await apiGetKPIsAndFields(accessToken, appContext.globalDataCache));
      } catch (error) {
        appContext.setError(error);
      }
    };

    void loadKPIs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  return (
    <GenericModal
      title={t('forms:Versioning.PreviewTitle', { name: props.taskType.name, version: props.taskType.version })}
      isOpen={props.isOpen}
      onClose={props.onDismiss}
      hideFooter
      titleSeparator
      minWidth={300}
      width={'min(94vw, 720px)'}
      maxWidth={'720px'}
      height={'min(86vh, 760px)'}
      maxHeight={'86vh'}
    >
      {/* THIS modal owns the scrolling, not the renderer: the form below is made inert, and an inert
          subtree is not hit-tested - a scroll pane inside it would never see the wheel, and a long
          form could not be scrolled at all. So the pane stays outside the inert part and the form
          flows to its natural height inside it (disableScrollContainer). */}
      <IPStack verticalFill styles={{ root: { height: '100%', minHeight: 0 } }}>
        <ScrollableStackItem minHeight={100}>
          {/* A form to READ, not to fill in: readonly still leaves a form that answers the pointer -
              the attachment link opens, the KPI badge flickers away under the cursor, a date picker
              offers itself. inert plus pointer-events takes all of that away, for the keyboard as
              well as the mouse, and leaves only the scrolling of the pane above. Same pair, and the
              same reason, as the locked document preview in ProjectDocumentsRoom. */}
          <div ref={(element) => element?.setAttribute('inert', '')} style={{ pointerEvents: 'none' }}>
            <TaskTypeFormRenderer
              task={task}
              links={[]}
              formElements={props.formElements}
              isLoading={false}
              // A locked design-time surface: the fields render as inputs, but nothing invites an
              // answer and no add or edit control appears (SingleFormElement only unlocks with
              // allowEdit).
              allowEdit={false}
              readonly={true}
              compact={false}
              designTime={true}
              disableScrollContainer={true}
              // Passed explicitly: an older version is not in the task type cache the renderer would
              // otherwise consult.
              isSystemForm={!!props.taskType.systemTaskType}
              kpis={kpis}
              onUpdateValue={() => {}}
              onUpdateKPI={() => {}}
              onSetKPI={() => {}}
              onUpdateTaskForForm={() => {}}
              addLinks={() => {}}
              setLinks={() => {}}
              removeLink={() => {}}
            />
          </div>
        </ScrollableStackItem>
      </IPStack>
    </GenericModal>
  );
};

export default FormVersionPreviewModal;
